import { Suspense } from 'react';
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Link,
} from 'react-router-dom';

import { customRoutes } from './lazy-load';
import RouteErrorBoundary from './RouteErrorBoundary';
import ProtectedComponent from './protectedComponent';
import ToastNotification from '@/components/common/ToastNotification';
import Loader from '@shared/components/Loader';
import StatusMessage from '@shared/components/StatusMessage';
import { AccountUiProtectedRoute, AnyPermissionProtectedRoute, PermissionProtectedRoute } from '../HOCs/routesHOCs';
import { Permission } from '@/constant/permissions';
import { ROUTE_URLS } from '@/constant/routeURLs';
import { JETCARD_LIST_TABS } from '@/pages/jet-card/jetCardListTabs';
import { TASK_LIST_TABS } from '@/pages/tasks/taskListTabs';
import { SYSTEM_SETTINGS_TYPE } from '@/pages/URTManagement/systemSettingsTabs';
import { DASHBOARD_TABS } from '@/pages/dashboard/dashboardTabs';
import { IS_ACCOUNT_DETAIL_PAGE_ENABLED, IS_TRIP_REPORTING_FEATURE_ENABLED } from '@/utils/constant';

export const maverickRoutes = createRoutesFromElements(
  <Route errorElement={<RouteErrorBoundary />}>
    <Route path={ROUTE_URLS.MOBILE_APP}>
      <Route path="*" element={<customRoutes.MobileApp />} />
    </Route>
    <Route path="/403" element={<customRoutes.UnAuthorized />} />
    <Route path="/login" element={<customRoutes.LoginScreen />} />
    <Route path={ROUTE_URLS.EMBED_JETCARD} element={<customRoutes.EmbedJetCard />} />
    <Route path={ROUTE_URLS.EMBED_OPPORTUNITY} element={<customRoutes.EmbedOpportunity />} />
    <Route path={ROUTE_URLS.EMBED_REDEEM} element={<customRoutes.EmbedRedeem />} />
    <Route path={ROUTE_URLS.EMBED_TRIP} element={<customRoutes.EmbedTrip />} />
    <Route path="jetcard/external-pages/initiation/:id" element={<customRoutes.Initiation />} />
    <Route path="jetcard/external-pages/thankyou" element={<customRoutes.Thankyou />} />
    <Route path={ROUTE_URLS.TRIP_PROPOSAL_PREVIEW_CLIENT} element={<customRoutes.TripProposalPreviewClient />} />
    <Route path={ROUTE_URLS.TRIP_PROPOSAL_PREVIEW_CLIENT_RECIPIENT} element={<customRoutes.TripProposalPreviewClientRecipient />} />
    <Route path={ROUTE_URLS.TRIP_PROPOSAL_PRELIMINARY_INVOICE_CLIENT} element={<customRoutes.TripProposalClientPreliminaryInvoice />} />
    <Route path={ROUTE_URLS.TRIP_PROPOSAL_INVOICE_CLIENT} element={<customRoutes.TripProposalClientInvoice />} />
    <Route path={ROUTE_URLS.TRIP_FEEDBACK_CLIENT} element={<customRoutes.TripFeedbackClient />} />
    <Route path="/" element={<customRoutes.Layout />}>
      <Route path="/" element={<customRoutes.HomeScreen />} />
      <Route path={ROUTE_URLS.DASHBOARD} element={<ProtectedComponent component={<customRoutes.DashboardScreen />} />}>
        <Route path={DASHBOARD_TABS.SALE} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_SALES_DASHBOARD ]} outlet={<customRoutes.BrokerSalesDashboard />} />} />
        <Route path={DASHBOARD_TABS.LEADERSHIP} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_LEADERSHIP_DASHBOARD ]} outlet={<customRoutes.LeadershipDashboard />} />} />
        <Route path={DASHBOARD_TABS.LEADERBOARD} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_LEADERBOARD_DASHBOARD ]} outlet={<customRoutes.LeaderboardDashboard />} />} />
        <Route path={DASHBOARD_TABS.CLIENT_ANALYTICS} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_SALES_DASHBOARD ]} outlet={<customRoutes.ClientAnalyticsDashboard />} />} />
        {/* Redirects for old URLs */}
        <Route path="sale-dashboard" element={<Navigate to={`${ROUTE_URLS.DASHBOARD}/${DASHBOARD_TABS.SALE}`} replace />} />
        <Route path="leaderboard-dashboard" element={<Navigate to={`${ROUTE_URLS.DASHBOARD}/${DASHBOARD_TABS.LEADERBOARD}`} replace />} />
        <Route path="leadership-dashboard" element={<Navigate to={`${ROUTE_URLS.DASHBOARD}/${DASHBOARD_TABS.LEADERSHIP}`} replace />} />
      </Route>
      <Route path="/system-setting" element={<customRoutes.URTManagement />}>
        <Route index element={<Navigate replace to={`${SYSTEM_SETTINGS_TYPE.ROLE}-management`} />} />
        <Route path={`${SYSTEM_SETTINGS_TYPE.ROLE}-management`} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_ROLE_MANAGEMENT ]} outlet={<customRoutes.RoleManagement />} />} />
        <Route path={`${SYSTEM_SETTINGS_TYPE.TEAM}-management`} element={<PermissionProtectedRoute permissionKeyList={[ Permission.MANAGE_TEAM ]} outlet={<customRoutes.TeamManagement />} />} />
        <Route path={`${SYSTEM_SETTINGS_TYPE.USER}-management`} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_USER_MANAGEMENT ]} outlet={<customRoutes.UserManagement />} />} />
        <Route path={`${SYSTEM_SETTINGS_TYPE.ACCOUNT}-management`} element={<customRoutes.AccountManagement />} />
        <Route path={`${SYSTEM_SETTINGS_TYPE.CONFIGURATION}-management`} element={<customRoutes.ConfigurationManagement />} />
      </Route>
      <Route path={ROUTE_URLS.CONTACT_DETAILS} element={<customRoutes.ContactDetails />}>
        <Route index path={ROUTE_URLS.CONTACT_DETAILS} element={<customRoutes.ContactDetailsDetailsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_TRIPS_TAB} element={<customRoutes.ContactDetailsTripsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_JETCARD_TAB} element={<customRoutes.ContactDetailsJetCardsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_JETCARD_TAB_WITH_ID} element={<customRoutes.ContactDetailsJetCardsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_FLIGHT_CREDITS_TAB} element={<customRoutes.ContactDetailsFlightCreditsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_PASSENGERS_TAB} element={<customRoutes.ContactDetailsPassengersTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_PETS_TAB} element={<customRoutes.ContactDetailsPetsTab />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_TASKS_TAB} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TASKS ]} outlet={<customRoutes.ContactDetailsTasksTab />} />} />
        <Route path={ROUTE_URLS.CONTACT_DETAILS_NOTES_TAB} element={<customRoutes.ContactDetailsNotesTab />} />
      </Route>
      { IS_ACCOUNT_DETAIL_PAGE_ENABLED && (
        <Route path={ROUTE_URLS.ACCOUNT_DETAILS} element={<AccountUiProtectedRoute outlet={<customRoutes.AccountDetails />} />}>
          <Route index path={ROUTE_URLS.ACCOUNT_DETAILS} element={<customRoutes.AccountDetailsDetailsTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_TRIPS_TAB} element={<customRoutes.AccountDetailsTripsTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_JETCARD_TAB} element={<customRoutes.AccountDetailsJetCardsTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_FLIGHT_CREDITS_TAB} element={<customRoutes.AccountDetailsFlightCreditsTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_PASSENGERS_TAB} element={<customRoutes.AccountDetailsPassengersTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_PETS_TAB} element={<customRoutes.AccountDetailsPetsTab />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_TASKS_TAB} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TASKS ]} outlet={<customRoutes.AccountDetailsTasksTab />} />} />
          <Route path={ROUTE_URLS.ACCOUNT_DETAILS_NOTES_TAB} element={<customRoutes.AccountDetailsNotesTab />} />
        </Route>
      )}
      <Route path="global-search-results" element={<customRoutes.GlobalSearchResults />} />
      <Route path="user-details" element={<customRoutes.UserDetails />} />
      <Route path="create-team" element={<customRoutes.CreateTeamScreen />} />
      <Route path="edit-team" element={<customRoutes.CreateTeamScreen />} />
      <Route path="team" element={<customRoutes.TeamScreen />} />
      <Route path="team-list" element={<customRoutes.TeamList />} />
      <Route path="create-role" element={<customRoutes.CreateRoleScreen />} />
      <Route path="edit-role" element={<customRoutes.CreateRoleScreen />} />
      <Route path={ROUTE_URLS.CONTACT_LIST} element={<customRoutes.ContactListScreen />} />
      { IS_ACCOUNT_DETAIL_PAGE_ENABLED && (
        <Route path={ROUTE_URLS.ACCOUNTS} element={<AccountUiProtectedRoute outlet={<customRoutes.AccountListScreen />} />} />
      )}
      <Route path={ROUTE_URLS.AIRCRAFTS_LIST} element={<customRoutes.AircraftListScreen />} />
      <Route path={ROUTE_URLS.OPERATORS_LIST} element={<customRoutes.AircraftListScreen />} />
      <Route path={ROUTE_URLS.FBO_LIST} element={<customRoutes.AircraftListScreen />} />
      <Route path={ROUTE_URLS.AIRPORTS_LIST} element={<customRoutes.AircraftListScreen />} />
      <Route path={ROUTE_URLS.AIRPORTS_FBOS_LIST} element={<customRoutes.AircraftListScreen />} />
      <Route path={ROUTE_URLS.TRIP} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP ]} outlet={<customRoutes.TripListing />} />} />
      <Route
        path={ROUTE_URLS.CALENDAR}
        element={<AnyPermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP, Permission.VIEW_TASKS ]} outlet={<customRoutes.Calendar />} />}
      />
      <Route
        path={ROUTE_URLS.CALENDAR_VIEW_ROUTE}
        element={<AnyPermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP, Permission.VIEW_TASKS ]} outlet={<customRoutes.Calendar />} />}
      />
      <Route path={ROUTE_URLS.TRIP_DETAILS} element={<customRoutes.TripDetails />}>
        <Route index path={ROUTE_URLS.TRIP_DETAILS} element={<customRoutes.TripDetailsDetailsTab />} />
        <Route path={ROUTE_URLS.TRIP_DETAILS_PASSENGERS_TAB} element={<customRoutes.TripDetailsPassengersTab />} />
        <Route path={ROUTE_URLS.TRIP_DETAILS_FINANCES_TAB} element={<customRoutes.TripDetailsFinancesTab />} />
        <Route path={ROUTE_URLS.TRIP_DETAILS_TASKS_TAB} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TASKS ]} outlet={<customRoutes.TripDetailsTasksTab />} />} />
        <Route path={ROUTE_URLS.TRIP_DETAILS_NOTES_TAB} element={<customRoutes.TripDetailsNotesTab />} />
        { IS_TRIP_REPORTING_FEATURE_ENABLED && (<Route path={ROUTE_URLS.TRIP_DETAILS_TRIP_REPORTS_TAB} element={<customRoutes.TripDetailsTripReportsTab />} />)}
        <Route path={ROUTE_URLS.TRIP_DETAILS_ACTIVITY_HISTORY_TAB} element={<customRoutes.TripDetailsActivityHistoryTab />} />
      </Route>
      <Route path={ROUTE_URLS.CREATE_TRIP} element={<PermissionProtectedRoute permissionKeyList={[ Permission.CREATE_TRIP ]} outlet={<customRoutes.ManageTrip />} />} />
      <Route path={ROUTE_URLS.EDIT_TRIP} element={<PermissionProtectedRoute permissionKeyList={[ Permission.CREATE_TRIP ]} outlet={<customRoutes.ManageTrip />} />} />
      <Route path={ROUTE_URLS.TRIP_PROPOSAL_PREVIEW_BROKER} element={<PermissionProtectedRoute permissionKeyList={[ Permission.CREATE_TRIP ]} outlet={<customRoutes.TripProposalPreviewBroker />} />} />
      <Route path={ROUTE_URLS.TRIP_FEEDBACK_BROKER} element={<customRoutes.TripFeedbackBroker />} />
      <Route path={ROUTE_URLS.FINANCE} element={<customRoutes.Finance />}>
        <Route index path={ROUTE_URLS.FINANCE} element={<NotFound />} />
        <Route path={ROUTE_URLS.FINANCE_OPERATOR_PAYMENT} element={<customRoutes.FinanceOperatorPayments />} />
      </Route>
      <Route
        path={ROUTE_URLS.FINANCE_TRIP_ADMINISTRATION}
        element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP_FINANCE_LIST ]} outlet={<customRoutes.FinanceTripAdministrationList />} />}
      />
      <Route path={ROUTE_URLS.TASKS} element={<customRoutes.TaskListContainer />}>
        <Route index element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TASKS ]} outlet={<customRoutes.Tasks />} />} />
        <Route
          path={TASK_LIST_TABS.TEMPLATES}
          element={<PermissionProtectedRoute permissionKeyList={[ Permission.MANAGE_TASK_TEMPLATES ]} outlet={<customRoutes.TaskTemplates />} />}
        />
        <Route
          path={`${TASK_LIST_TABS.TEMPLATES}/new`}
          element={<PermissionProtectedRoute permissionKeyList={[ Permission.MANAGE_TASK_TEMPLATES ]} outlet={<customRoutes.TaskTemplateEditor />} />}
        />
        <Route
          path={`${TASK_LIST_TABS.TEMPLATES}/:id`}
          element={<PermissionProtectedRoute permissionKeyList={[ Permission.MANAGE_TASK_TEMPLATES ]} outlet={<customRoutes.TaskTemplateEditor />} />}
        />
      </Route>
      <Route
        path={ROUTE_URLS.FLIGHTS}
        element={<ProtectedComponent component={<customRoutes.FlightTrackingPage />} />}
      />
      <Route path="jet-card" element={<customRoutes.JetCard />} />
      <Route path="jetcard-list" element={<Navigate to={ROUTE_URLS.JETCARDS} replace />} />
      <Route path={ROUTE_URLS.JETCARDS} element={<customRoutes.JetCardList />}>
        <Route index element={<customRoutes.JetCardAgreements />} />
        <Route path={JETCARD_LIST_TABS.PROMOTIONS} element={<customRoutes.JetCardPromotionsList />} />
        <Route path={JETCARD_LIST_TABS.ADDITIONAL_TERMS} element={<customRoutes.JetCardAdditionalTermsList />} />
        <Route path={JETCARD_LIST_TABS.PROMOTIONAL_TERMS} element={<customRoutes.JetCardPromotionalTermsList />} />
        <Route path={JETCARD_LIST_TABS.STATEMENTS} element={<customRoutes.JetCardStatementList />} />
      </Route>
      <Route path="create-jetcard" element={<customRoutes.CreateJetCard />} />
      <Route path="edit-jetcard" element={<customRoutes.CreateJetCard />} />
      <Route path="jetcard-detail" element={<customRoutes.CreateJetCard />} />
      <Route path="jetcard/external-pages/preview/:id" element={<customRoutes.Initiation />} />
      <Route path="*" element={<NotFound />} />
      <Route path="contacts/jetcard/promotions-list" element={<customRoutes.JetCardPromotionsList />} />
      <Route path="notifications" element={<customRoutes.Notifications />} />
      <Route path="jetcard/promotions-list" element={<customRoutes.JetCardPromotionsList />} />
      <Route path="contacts/jetcard/jet-cards-list" element={<customRoutes.JetCardAgreements />} />
      { IS_TRIP_REPORTING_FEATURE_ENABLED && (<Route path={ROUTE_URLS.TRIP_REPORTS} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP_REPORTS ]} outlet={<customRoutes.TripReports />} />} />)}
      { IS_TRIP_REPORTING_FEATURE_ENABLED && (<Route path={ROUTE_URLS.TRIP_REPORT_DETAILS} element={<PermissionProtectedRoute permissionKeyList={[ Permission.VIEW_TRIP_REPORTS ]} outlet={<customRoutes.TripReportDetails />} />} />)}
    </Route>
  </Route>
);

const router = createBrowserRouter(maverickRoutes);

export default function Navigation() {
  return (
    <div>
      <div>
        <Suspense fallback={<Loader />}>
          <RouterProvider router={router} />
        </Suspense>
      </div>
      <ToastNotification />
    </div>
  );
}

function NotFound() {
  return (
    <StatusMessage
      title="Page not found"
      description="The page you’re looking for doesn’t exist or has moved."
      action={<Link to="/" className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary text-decoration-none">Go to home</Link>}
    />
  );
}
